import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faUserCheck, faUserSecret } from '@fortawesome/pro-solid-svg-icons';
import Drawer from '../../Drawer/Drawer';
import { useInfiniteStoryViewers } from '../../../../queries/ephemeralStories';
import StoryViewerListItem from './StoryViewerListItem';
import './StoryViewersModal.scss';

// Modale enregistrée dans modalTypes (cf. src/index.js).
// Reçoit `storyId`, `handleClose`, `toggle` du ModalManager.
// `totalViews` (optionnel) = count.view de la story (= membres + guests cumulés). Permet d'afficher
// le delta des vues anonymes en plus de la liste des membres listés via /viewers.
//
// Le parent (StoryViewer) détecte la fermeture en écoutant `togglePopup` du ModalContext —
// pas besoin d'un callback onClose ici.
const StoryViewersModal = ({ storyId, handleClose, totalViews = 0 }) => {
    const sentinelRef = useRef(null);

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteStoryViewers(storyId);

    // Infinite scroll via IntersectionObserver sur le sentinel en bas de liste.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasNextPage) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && !isFetchingNextPage) {
                fetchNextPage();
            }
        }, { rootMargin: '120px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const viewers = (data?.pages || []).flatMap((p) => p?.data || []);
    const total = data?.pages?.[0]?.total || 0;
    // count.view inclut membres + guests ; le delta nous donne les vues anonymes.
    const guestViews = Math.max(0, (totalViews || 0) - total);
    const grandTotal = Math.max(totalViews || 0, total);
    const hasBreakdown = total > 0 || guestViews > 0;

    return (
        <Drawer open={true} small handleClose={handleClose} headContent={
            <div className="story-viewers-modal-header">
                <div className="story-viewers-modal-header-main">
                    <FontAwesomeIcon icon={faEye} className="story-viewers-modal-header-icon"/>
                    <span className="story-viewers-modal-header-count">{grandTotal}</span>
                    <span className="story-viewers-modal-header-label">
                        Vue(s)
                    </span>
                </div>
                {hasBreakdown && (
                    <div className="story-viewers-modal-header-breakdown">
                        {total > 0 && (
                            <span className="story-viewers-modal-chip">
                                <FontAwesomeIcon icon={faUserCheck}/>
                                {total} membre{total > 1 ? 's' : ''}
                            </span>
                        )}
                        {guestViews > 0 && (
                            <span className="story-viewers-modal-chip is-guest">
                                <FontAwesomeIcon icon={faUserSecret}/>
                                {guestViews} anonyme{guestViews > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                )}
            </div>
        }>
            <div className="story-viewers-modal">
                {isLoading && (
                    <div className="story-viewers-modal-state">Chargement…</div>
                )}

                {isError && !isLoading && (
                    <div className="story-viewers-modal-state is-error">
                        Impossible de charger les viewers pour le moment.
                    </div>
                )}

                {!isLoading && !isError && viewers.length === 0 && (
                    <div className="story-viewers-modal-state">
                        {guestViews > 0 ? (
                            <>
                                <FontAwesomeIcon icon={faUserSecret} className="story-viewers-modal-state-icon"/>
                                <p>{guestViews} visiteur{guestViews > 1 ? 's' : ''} anonyme{guestViews > 1 ? 's' : ''} {guestViews > 1 ? 'ont vu' : 'a vu'} ta story.</p>
                                <p className="story-viewers-modal-state-hint">Seuls les membres inscrits apparaissent dans cette liste.</p>
                            </>
                        ) : (
                            <>Personne n'a encore vu cette story.</>
                        )}
                    </div>
                )}

                {viewers.length > 0 && (
                    <ul className="story-viewers-modal-list">
                        {viewers.map((view) => (
                            <StoryViewerListItem
                                key={view._id}
                                viewer={view.viewer}
                                createdAt={view.createdAt}
                                onClose={handleClose}
                            />
                        ))}
                        <li ref={sentinelRef} className="story-viewers-modal-sentinel" aria-hidden="true"/>
                        {isFetchingNextPage && (
                            <li className="story-viewers-modal-state">Chargement…</li>
                        )}
                    </ul>
                )}
            </div>
        </Drawer>
    );
};

export default StoryViewersModal;
